import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { removePartner, leaveHousehold, deleteMyAccount, exportMyData, startFresh } from "../lib/api";
import { Loader2, Copy, Check, Users, Share2, UserMinus, LogOut, Download, Trash2, User, RotateCcw } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";

function roleLabel(user) {
  if (user.partner) return user.role === "owner" ? "Owner" : "Member";
  return "Solo";
}

export function ProfileCard() {
  const { user, logout } = useAuth();
  if (!user) return null;
  return (
    <div className="rounded-2xl bg-[var(--surface)] border border-[var(--line)] shadow-[0_2px_8px_rgba(0,0,0,0.04)] p-6 md:p-7 mb-6" data-testid="profile-card">
      <div className="flex items-center gap-2 mb-4">
        <User size={16} className="text-[var(--pine)]" strokeWidth={1.9} />
        <h2 className="text-sm uppercase tracking-[0.18em] font-bold text-[var(--ink3)]">Profile</h2>
      </div>
      <div className="flex items-center gap-3 mb-5">
        <div className="h-11 w-11 rounded-full bg-[var(--pine)] text-[var(--on-pine)] flex items-center justify-center text-base font-semibold shrink-0">
          {(user.name || "?").charAt(0).toUpperCase()}
        </div>
        <div className="min-w-0">
          <p className="text-[var(--ink)] font-semibold truncate" data-testid="profile-name">{user.name}</p>
          <p className="text-sm text-[var(--ink3)] truncate" data-testid="profile-email">{user.email}</p>
        </div>
        <span className="ml-auto rounded-full bg-[var(--line2)] px-3 py-1 text-xs font-semibold text-[var(--ink2)]" data-testid="profile-role">
          {roleLabel(user)}
        </span>
      </div>
      <button
        data-testid="profile-signout-btn"
        onClick={logout}
        className="inline-flex items-center gap-2 rounded-full border border-[var(--line)] bg-[var(--surface)] px-4 py-2 text-sm font-semibold text-[var(--ink3)] hover:text-[var(--pine)] hover:border-[var(--pine)] transition-all duration-200"
      >
        <LogOut size={15} strokeWidth={2} />
        Sign out
      </button>
    </div>
  );
}

export function HouseholdCard() {
  const { user, refresh } = useAuth();
  const [copied, setCopied] = useState(false);
  const [confirming, setConfirming] = useState(false);   // owner removing partner
  const [leaving, setLeaving] = useState(false);          // member leave modal
  const [busy, setBusy] = useState(false);
  // The inviter is often watching this page when their partner accepts — refresh on focus so the
  // new member appears without a hard reload.
  useEffect(() => {
    const onFocus = () => { refresh && refresh(); };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onFocus);
    };
  }, [refresh]);
  if (!user) return null;
  const full = !!user.partner;
  const isOwner = user.role === "owner";

  const doRemove = () => {
    setBusy(true);
    removePartner(user.partner?.id)
      .then(() => {
        toast.success(`${user.partner?.name || "Partner"} removed. You can invite someone new.`);
        setConfirming(false);
        return refresh && refresh();
      })
      .catch(() => toast.error("Couldn't remove your partner. Please try again."))
      .finally(() => setBusy(false));
  };

  const doLeave = () => {
    setBusy(true);
    leaveHousehold()
      .then((res) => {
        if (res.left_household === false) {
          toast.message("You're already in your own household.");
        } else {
          toast.success("You've left the household. Your private items moved with you.");
        }
        setLeaving(false);
        return refresh && refresh();
      })
      .catch(() => toast.error("Couldn't leave the household. Please try again."))
      .finally(() => setBusy(false));
  };

  const copy = () => {
    navigator.clipboard?.writeText(user.invite_code);
    setCopied(true);
    toast.success("Invite code copied.");
    setTimeout(() => setCopied(false), 1800);
  };

  const inviteLink = `${window.location.origin}/join?code=${user.invite_code}`;
  const copyLink = () => {
    navigator.clipboard?.writeText(inviteLink);
    toast.success("Invite link copied.");
  };
  const share = async () => {
    const data = { title: "Heldall", text: "Join my household on Heldall.", url: inviteLink };
    const isMobile = /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
    if (navigator.share && isMobile) {
      try { await navigator.share(data); return; }
      catch (err) { if (err && err.name === "AbortError") return; }
    }
    copyLink();
  };

  return (
    <div className="rounded-2xl bg-[var(--surface)] border border-[var(--line)] shadow-[0_2px_8px_rgba(0,0,0,0.04)] p-6 md:p-7 mb-6" data-testid="household-card">
      <div className="flex items-center gap-2 mb-4">
        <Users size={16} className="text-[var(--pine)]" strokeWidth={1.9} />
        <h2 className="text-sm uppercase tracking-[0.18em] font-bold text-[var(--ink3)]">{full ? (user.household_name || "Your household") : "Household"}</h2>
      </div>

      <div className="rounded-xl bg-[var(--surface2)] border border-[var(--line2)] divide-y divide-[var(--line2)] mb-5">
        {user.members?.map((m) => (
          <div key={m.id} className="flex items-center gap-3 px-3.5 py-3" data-testid={`household-member-${m.role}`}>
            <div className="h-8 w-8 rounded-full bg-[var(--pine)] text-[var(--on-pine)] flex items-center justify-center text-xs font-semibold">
              {(m.name || "?").charAt(0).toUpperCase()}
            </div>
            <div className="min-w-0">
              <p className="text-sm font-medium text-[var(--ink)]">{m.name}{m.id === user.id ? " (you)" : ""}</p>
              <p className="text-xs text-[var(--ink4)]">{m.email} · {m.role}</p>
            </div>
          </div>
        ))}
      </div>

      {full ? (
        <>
          <p className="text-sm text-[var(--ink3)]" data-testid="household-full">
            {isOwner
              ? `You and ${user.partner.name} share this household. Anything can move between you.`
              : `You're a member of this household with ${user.partner.name}. Anything can move between you.`}
          </p>
          {isOwner ? (
            <button
              data-testid="remove-partner-btn"
              onClick={() => setConfirming(true)}
              className="mt-4 inline-flex items-center gap-2 rounded-full border border-[var(--line)] bg-[var(--surface)] px-4 py-2 text-sm font-semibold text-[var(--ink3)] hover:border-[var(--danger)] hover:text-[var(--danger)] transition-all duration-200"
            >
              <UserMinus size={15} strokeWidth={2} />
              Remove {user.partner.name} from household
            </button>
          ) : (
            <button
              data-testid="leave-household-btn"
              onClick={() => setLeaving(true)}
              className="mt-4 inline-flex items-center gap-2 rounded-full border border-[var(--line)] bg-[var(--surface)] px-4 py-2 text-sm font-semibold text-[var(--ink3)] hover:border-[var(--danger)] hover:text-[var(--danger)] transition-all duration-200"
            >
              <LogOut size={15} strokeWidth={2} />
              Leave household
            </button>
          )}
        </>
      ) : (
        <div data-testid="invite-section">
          <p className="text-sm text-[var(--ink2)] bg-[var(--surface2)] border border-[var(--line2)] rounded-xl px-3.5 py-2.5 mb-4" data-testid="solo-note">
            You can use Heldall by yourself, or invite someone when you're ready.
          </p>
          <p className="text-[var(--ink)] font-medium mb-1">Invite someone</p>
          <p className="text-sm text-[var(--ink3)] mb-3">One tap to share a join link. They won't need to type anything.</p>
          <button
            data-testid="share-invite-btn"
            onClick={share}
            className="inline-flex items-center gap-2 rounded-full bg-[var(--pine)] hover:bg-[var(--pine-dark)] text-[var(--on-pine)] font-semibold px-5 py-2.5 text-sm shadow-[0_8px_24px_rgba(44,90,77,0.25)] transition-all duration-200 mb-4"
          >
            <Share2 size={16} strokeWidth={2} />
            Share invite
          </button>
          <p className="text-xs text-[var(--ink4)] mb-2">Or share the code manually</p>
          <div className="flex items-center gap-2">
            <span data-testid="invite-code" className="rounded-xl bg-[var(--line2)] border border-[var(--line)] px-4 py-2.5 text-lg font-bold tracking-[0.3em] text-[var(--ink)]">
              {user.invite_code}
            </span>
            <button
              data-testid="copy-invite-btn"
              onClick={copy}
              className="inline-flex items-center gap-1.5 rounded-xl border border-[var(--line)] bg-[var(--surface)] px-4 py-2.5 text-sm font-semibold text-[var(--ink2)] hover:border-[var(--pine)] hover:text-[var(--pine)] transition-all duration-200"
            >
              {copied ? <Check size={15} strokeWidth={2.2} /> : <Copy size={15} strokeWidth={2} />}
              {copied ? "Copied" : "Copy"}
            </button>
          </div>
        </div>
      )}

      {confirming && full && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm" data-testid="remove-partner-modal">
          <div className="w-full max-w-md rounded-2xl bg-[var(--surface)] border border-[var(--line)] shadow-2xl p-6 md:p-7">
            <h3 className="font-serif-display text-xl text-[var(--ink)] mb-3">Remove {user.partner.name} from your household?</h3>
            <div className="text-sm text-[var(--ink3)] space-y-2 mb-6">
              <p>{user.partner.name} will no longer be able to see shared household items or receive new handoffs.</p>
              <p>Your private items will stay private.</p>
              <p>Items you created will stay with you.</p>
              <p>Items currently handed to {user.partner.name} will return to you.</p>
              <p className="text-[var(--ink4)]">This won't delete {user.partner.name}'s account.</p>
            </div>
            <div className="flex items-center justify-end gap-3">
              <button data-testid="remove-partner-cancel" onClick={() => setConfirming(false)} disabled={busy}
                className="rounded-full border border-[var(--line)] bg-[var(--surface)] px-5 py-2.5 text-sm font-semibold text-[var(--ink2)] hover:border-[var(--pine)] disabled:opacity-50 transition-all duration-200">
                Cancel
              </button>
              <button data-testid="remove-partner-confirm" onClick={doRemove} disabled={busy}
                className="inline-flex items-center gap-2 rounded-full bg-[var(--danger)] hover:bg-[var(--danger-dark)] text-[var(--on-pine)] px-5 py-2.5 text-sm font-semibold disabled:opacity-50 transition-colors duration-200">
                {busy && <Loader2 size={15} className="animate-spin" />}
                Remove {user.partner.name}
              </button>
            </div>
          </div>
        </div>
      )}

      {leaving && full && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm" data-testid="leave-household-modal">
          <div className="w-full max-w-md rounded-2xl bg-[var(--surface)] border border-[var(--line)] shadow-2xl p-6 md:p-7">
            <h3 className="font-serif-display text-xl text-[var(--ink)] mb-3">Leave this household?</h3>
            <div className="text-sm text-[var(--ink3)] space-y-2 mb-6">
              <p>You'll no longer see shared household items or receive new handoffs from this household.</p>
              <p>Your private items will move with you.</p>
              <p>Items handed to you will return to their creator.</p>
              <p>Shared household items you created will stay in the household.</p>
              <p className="text-[var(--ink4)]">This won't delete your account.</p>
            </div>
            <div className="flex items-center justify-end gap-3">
              <button data-testid="leave-household-cancel" onClick={() => setLeaving(false)} disabled={busy}
                className="rounded-full border border-[var(--line)] bg-[var(--surface)] px-5 py-2.5 text-sm font-semibold text-[var(--ink2)] hover:border-[var(--pine)] disabled:opacity-50 transition-all duration-200">
                Cancel
              </button>
              <button data-testid="leave-household-confirm" onClick={doLeave} disabled={busy}
                className="inline-flex items-center gap-2 rounded-full bg-[var(--danger)] hover:bg-[var(--danger-dark)] text-[var(--on-pine)] px-5 py-2.5 text-sm font-semibold disabled:opacity-50 transition-colors duration-200">
                {busy && <Loader2 size={15} className="animate-spin" />}
                Leave household
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function downloadExport() {
  return exportMyData()
    .then(({ blob, filename }) => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Your data is downloading.");
    })
    .catch(() => toast.error("Couldn't export your data. Please try again."));
}

export function AccountCard() {
  const { user, logout } = useAuth();
  const [step, setStep] = useState(null); // null | "export" | "confirm" | "done"
  const [deleting, setDeleting] = useState(false);
  const [freshOpen, setFreshOpen] = useState(false);
  const [freshConfirm, setFreshConfirm] = useState("");
  const [freshing, setFreshing] = useState(false);
  if (!user) return null;
  const ownerWithPartner = user.role === "owner" && !!user.partner;
  const isSolo = !user.partner;

  const goToHousehold = () => {
    setStep(null);
    document.querySelector('[data-testid="household-card"]')?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const closeFresh = () => { setFreshOpen(false); setFreshConfirm(""); };
  const doStartFresh = () => {
    setFreshing(true);
    startFresh()
      .then((res) => {
        if (res.blocked) { toast.error(res.message || "Start fresh isn't available right now."); closeFresh(); return; }
        // Prevent the first-capture proof moment from replaying for this returning user.
        if (user.id) localStorage.setItem(`heldall_proof_${user.id}`, "1");
        const n = res.cleared || 0;
        toast.success(n > 0
          ? `Cleared ${n} ${n === 1 ? "item" : "items"}. You can restore them from Recently deleted.`
          : "You're already starting fresh — nothing to clear.");
        closeFresh();
      })
      .catch(() => toast.error("Couldn't start fresh. Please try again."))
      .finally(() => setFreshing(false));
  };

  const doDelete = () => {
    setDeleting(true);
    deleteMyAccount()
      .then((res) => {
        if (res.blocked) { toast.error(res.message || "Remove your partner first."); return; }
        setStep("done");
        setTimeout(async () => { await logout(); window.location.href = "/"; }, 3000);
      })
      .catch(() => toast.error("Couldn't delete your account. Please try again."))
      .finally(() => setDeleting(false));
  };

  return (
    <div className="rounded-2xl bg-[var(--surface)] border border-[var(--line)] shadow-[0_2px_8px_rgba(0,0,0,0.04)] p-6 md:p-7 mb-6" data-testid="account-card">
      <h2 className="font-serif-display text-lg text-[var(--ink)] mb-1">Your data</h2>
      <p className="text-sm text-[var(--ink3)] mb-5">Take your data with you, or close your account.</p>

      <button data-testid="export-data-btn" onClick={downloadExport}
        className="w-full sm:w-auto inline-flex items-center gap-2 rounded-full border border-[var(--line)] bg-[var(--surface)] px-4 py-2.5 text-sm font-semibold text-[var(--ink2)] hover:border-[var(--pine)] hover:text-[var(--pine)] transition-all duration-200 mb-3">
        <Download size={15} strokeWidth={2} />
        Export my data
      </button>
      {isSolo && (
        <div>
          <button data-testid="start-fresh-btn" onClick={() => setFreshOpen(true)}
            className="inline-flex items-center gap-2 rounded-full px-4 py-2.5 text-sm font-semibold text-[var(--ink3)] hover:text-[var(--pine)] transition-colors duration-200 mb-1">
            <RotateCcw size={15} strokeWidth={2} />
            Start fresh
          </button>
          <p className="text-xs text-[var(--ink4)] mb-2 px-1">Clears everything to a clean slate. Items move to Recently deleted, where you can restore them.</p>
        </div>
      )}
      <div>
        <button data-testid="delete-account-btn" onClick={() => setStep("export")}
          className="inline-flex items-center gap-2 rounded-full px-4 py-2.5 text-sm font-semibold text-[var(--ink3)] hover:text-[var(--danger)] transition-colors duration-200">
          <Trash2 size={15} strokeWidth={2} />
          Close my account
        </button>
      </div>

      {freshOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm" data-testid="start-fresh-modal">
          <div className="w-full max-w-md rounded-2xl bg-[var(--surface)] border border-[var(--line)] shadow-2xl p-6 md:p-7">
            <h3 className="font-serif-display text-xl text-[var(--ink)] mb-3">Start fresh?</h3>
            <div className="text-sm text-[var(--ink3)] space-y-2 mb-5">
              <p>This clears your open items and your done items. You get a clean slate.</p>
              <p>Nothing is permanently deleted. Everything moves to <span className="font-semibold text-[var(--ink2)]">Recently deleted</span>, where you can restore it.</p>
              <p>Your account, settings, and invite code stay exactly as they are.</p>
            </div>
            <label className="block text-sm text-[var(--ink2)] mb-2">Type <span className="font-bold tracking-wide">START FRESH</span> to confirm.</label>
            <input
              data-testid="start-fresh-input"
              value={freshConfirm}
              onChange={(e) => setFreshConfirm(e.target.value)}
              placeholder="START FRESH"
              className="w-full rounded-xl border border-[var(--line)] bg-[var(--surface2)] px-3.5 py-2.5 text-sm text-[var(--ink)] focus:outline-none focus:border-[var(--pine)] mb-6"
            />
            <div className="flex items-center justify-end gap-3">
              <button data-testid="start-fresh-cancel" onClick={closeFresh} disabled={freshing}
                className="rounded-full border border-[var(--line)] bg-[var(--surface)] px-5 py-2.5 text-sm font-semibold text-[var(--ink2)] hover:border-[var(--pine)] disabled:opacity-50 transition-all duration-200">
                Cancel
              </button>
              <button data-testid="start-fresh-confirm" onClick={doStartFresh}
                disabled={freshing || freshConfirm.trim() !== "START FRESH"}
                className="inline-flex items-center gap-2 rounded-full bg-[var(--pine)] hover:bg-[var(--pine-dark)] text-[var(--on-pine)] px-5 py-2.5 text-sm font-semibold disabled:opacity-40 transition-colors duration-200">
                {freshing && <Loader2 size={15} className="animate-spin" />}
                Start fresh
              </button>
            </div>
          </div>
        </div>
      )}

      {step && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm" data-testid="delete-account-modal">
          <div className="w-full max-w-md rounded-2xl bg-[var(--surface)] border border-[var(--line)] shadow-2xl p-6 md:p-7">

            {step === "export" && (
              <div data-testid="delete-step-export">
                <h3 className="font-serif-display text-xl text-[var(--ink)] mb-3">Export first?</h3>
                <p className="text-sm text-[var(--ink3)] mb-6">Before you delete, you may want to export your data.</p>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-end gap-3">
                  <button data-testid="delete-export-cancel" onClick={() => setStep("confirm")}
                    className="rounded-full border border-[var(--line)] bg-[var(--surface)] px-5 py-2.5 text-sm font-semibold text-[var(--ink2)] hover:border-[var(--pine)] transition-all duration-200 order-2 sm:order-1">
                    Continue without exporting
                  </button>
                  <button data-testid="delete-export-now" onClick={downloadExport}
                    className="inline-flex items-center justify-center gap-2 rounded-full bg-[var(--pine)] hover:bg-[var(--pine-dark)] text-[var(--on-pine)] px-5 py-2.5 text-sm font-semibold transition-all duration-200 order-1 sm:order-2">
                    <Download size={15} strokeWidth={2} />
                    Export my data
                  </button>
                </div>
              </div>
            )}

            {step === "confirm" && ownerWithPartner && (
              <div data-testid="delete-step-blocked">
                <h3 className="font-serif-display text-xl text-[var(--ink)] mb-3">One step first</h3>
                <p className="text-sm text-[var(--ink3)] mb-6">To delete your account, remove {user.partner.name} from your household first.</p>
                <div className="flex items-center justify-end gap-3">
                  <button data-testid="delete-blocked-cancel" onClick={() => setStep(null)}
                    className="rounded-full border border-[var(--line)] bg-[var(--surface)] px-5 py-2.5 text-sm font-semibold text-[var(--ink2)] hover:border-[var(--pine)] transition-all duration-200">
                    Cancel
                  </button>
                  <button data-testid="delete-blocked-go-household" onClick={goToHousehold}
                    className="rounded-full bg-[var(--pine)] hover:bg-[var(--pine-dark)] text-[var(--on-pine)] px-5 py-2.5 text-sm font-semibold transition-all duration-200">
                    Go to household settings
                  </button>
                </div>
              </div>
            )}

            {step === "confirm" && !ownerWithPartner && (
              <div data-testid="delete-step-confirm">
                <h3 className="font-serif-display text-xl text-[var(--ink)] mb-3">Delete your account?</h3>
                <div className="text-sm text-[var(--ink3)] space-y-2 mb-6">
                  <p>This will permanently delete your private items and account details.</p>
                  <p>Shared household items you created may remain for the household unless you delete them first.</p>
                  <p>Items handed to you will return to their creator.</p>
                  <p>You can export your data before deleting.</p>
                  <p>Support can reactivate your account within 14 days, but deleted private items cannot be restored.</p>
                </div>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-end gap-3">
                  <button data-testid="delete-confirm-export" onClick={downloadExport} disabled={deleting}
                    className="inline-flex items-center justify-center gap-2 rounded-full border border-[var(--line)] bg-[var(--surface)] px-5 py-2.5 text-sm font-semibold text-[var(--ink2)] hover:border-[var(--pine)] disabled:opacity-50 transition-all duration-200 order-2 sm:order-1">
                    <Download size={15} strokeWidth={2} />
                    Export my data first
                  </button>
                  <button data-testid="delete-confirm-delete" onClick={doDelete} disabled={deleting}
                    className="inline-flex items-center justify-center gap-2 rounded-full bg-[var(--danger)] hover:bg-[var(--danger-dark)] text-[var(--on-pine)] px-5 py-2.5 text-sm font-semibold disabled:opacity-50 transition-colors duration-200 order-1 sm:order-2">
                    {deleting && <Loader2 size={15} className="animate-spin" />}
                    Delete my account
                  </button>
                </div>
              </div>
            )}

            {step === "done" && (
              <div className="text-center py-2" data-testid="delete-step-done">
                <div className="h-12 w-12 mx-auto mb-4 rounded-full bg-[var(--pine-soft)] flex items-center justify-center">
                  <Check size={22} className="text-[var(--pine)]" strokeWidth={2.2} />
                </div>
                <h3 className="font-serif-display text-xl text-[var(--ink)] mb-2">Your account is now inactive.</h3>
                <p className="text-sm text-[var(--ink3)]">You have 14 days to contact support if you change your mind. Private items deleted today cannot be restored.</p>
              </div>
            )}

          </div>
        </div>
      )}
    </div>
  );
}
